import { Injectable, inject } from '@angular/core';
import {
  type AddOnKey,
  AddOnOverrideState,
  type IAddOnOverride,
  type IAddOnOverrideClear,
  type IManagementUser,
  type IOrganisationSubscription,
  type ISubscriptionAddOn,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  Firestore,
  type WithRef,
  snapshot,
  toTimestamp,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { Moment } from 'moment-timezone';
import { ManagementService } from '../auth/management.service';

export class ForbiddenError extends Error {
  constructor(message = 'Not authorized: ManagementUser required') {
    super(message);
    this.name = 'ForbiddenError';
  }
}

export class AddOnNotEnabledError extends Error {
  constructor(addOnKey: string) {
    super(`Product ${addOnKey} not found in org's addOns`);
    this.name = 'AddOnNotEnabledError';
  }
}

export class SubscriptionNotFoundError extends Error {
  constructor() {
    super('Subscription not found');
    this.name = 'SubscriptionNotFoundError';
  }
}

export interface ISetOverrideOptions {
  reason?: string;
  expiresAt?: Moment;
}

/**
 * Client-side CRUD for `IAddOnOverride` records stored on an
 * organisation's primary subscription document.
 *
 * Write operations are gated to `ManagementUser` only. Non-management callers
 * receive a `ForbiddenError` before any Firestore I/O occurs. Server-side
 * Security Rules enforce the same constraint as defence-in-depth.
 */
@Injectable({ providedIn: 'root' })
export class AddOnOverrideService {
  private readonly _management = inject(ManagementService);

  /**
   * Set or replace an override for a product on the given subscription.
   * ManagementUser only.
   *
   * Throws:
   * - `ForbiddenError` if the current user is not a ManagementUser.
   * - `SubscriptionNotFoundError` if the subscription document is missing.
   * - `AddOnNotEnabledError` if `addOnKey` is not present in the
   *   subscription's `addOns` array (Stripe must already carry the
   *   product for an override to be meaningful).
   */
  async setOverride(
    subscriptionRef: DocumentReference<IOrganisationSubscription>,
    addOnKey: AddOnKey,
    state: AddOnOverrideState,
    options?: ISetOverrideOptions
  ): Promise<void> {
    const managementUser = await this._requireManagementUser();
    if (isForceState(state)) {
      const trimmedReason = options?.reason?.trim();
      if (!trimmedReason) {
        throw new Error('Reason required for FORCE override');
      }
    }

    const override = buildOverride(managementUser, state, options);
    await this._updateAddOn(subscriptionRef, addOnKey, (addOn) => ({
      ...addOn,
      override,
    }));
  }

  /**
   * Clear (remove) an override for a product on the given subscription.
   * ManagementUser only. Stamps `lastOverrideClear` so the subscription-write
   * audit trigger attributes the clear to this user, with this reason.
   *
   * Throws:
   * - `Error('Reason required to clear override')` if `reason` is empty or
   *   whitespace-only.
   * - `ForbiddenError` if the current user is not a ManagementUser.
   * - `SubscriptionNotFoundError` if the subscription document is missing.
   * - `AddOnNotEnabledError` if `addOnKey` is not present in the
   *   subscription's `addOns` array.
   */
  async clearOverride(
    subscriptionRef: DocumentReference<IOrganisationSubscription>,
    addOnKey: AddOnKey,
    reason: string
  ): Promise<void> {
    const managementUser = await this._requireManagementUser();
    if (!reason || reason.trim().length === 0) {
      throw new Error('Reason required to clear override');
    }

    const lastOverrideClear: IAddOnOverrideClear = {
      clearedBy: managementUser.ref,
      clearedAt: toTimestamp(),
      reason: reason.trim(),
    };
    await this._updateAddOn(subscriptionRef, addOnKey, (addOn) => ({
      ...omitOverride(addOn),
      lastOverrideClear,
    }));
  }

  private async _requireManagementUser(): Promise<WithRef<IManagementUser>> {
    const managementUser = await snapshot(this._management.user$);
    if (!managementUser) {
      throw new ForbiddenError();
    }
    return managementUser;
  }

  /**
   * Read-modify-write of one add-on inside a transaction, so a webhook
   * reconcile or another operator committing between the read and the write
   * cannot be silently reverted by this whole-array write.
   */
  private async _updateAddOn(
    subscriptionRef: DocumentReference<IOrganisationSubscription>,
    addOnKey: AddOnKey,
    transform: (addOn: ISubscriptionAddOn) => ISubscriptionAddOn
  ): Promise<void> {
    await Firestore.runTransaction(async (transaction) => {
      const subscription = await Firestore.safeGetDoc(
        subscriptionRef,
        transaction
      );
      if (!subscription) {
        throw new SubscriptionNotFoundError();
      }
      const addOns = subscription.addOns ?? [];
      if (!addOns.some((addOn) => addOn.addOnKey === addOnKey)) {
        throw new AddOnNotEnabledError(addOnKey);
      }

      const updated = addOns.map((addOn) =>
        addOn.addOnKey === addOnKey ? transform(addOn) : addOn
      );
      await Firestore.updateDoc(
        subscriptionRef,
        { addOns: updated },
        transaction
      );
    });
  }
}

function buildOverride(
  managementUser: WithRef<IManagementUser>,
  state: AddOnOverrideState,
  options?: ISetOverrideOptions
): IAddOnOverride {
  const base: IAddOnOverride = {
    state,
    setBy: managementUser.ref,
    setAt: toTimestamp(moment()),
  };
  if (options?.reason !== undefined) {
    base.reason = options.reason;
  }
  if (options?.expiresAt) {
    base.expiresAt = toTimestamp(moment(options.expiresAt));
  }
  return base;
}

function omitOverride(product: ISubscriptionAddOn): ISubscriptionAddOn {
  const productWithoutOverride = { ...product };
  delete productWithoutOverride.override;
  return productWithoutOverride;
}

function isForceState(state: AddOnOverrideState): boolean {
  return (
    state === AddOnOverrideState.ForceEnabled ||
    state === AddOnOverrideState.ForceDisabled
  );
}
